import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import type { MiceProjectView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Card, formatRawDate, PageSectionHead, Scaffold, ToneChip } from '@web/ui/core';
import { useAuthz } from '@web/ui/core/hooks/use_authz';
import { EventSetupChecklist } from '../components/event_setup_checklist';
import { FILTERS_RAIL_ID } from '../components/filters_rail';
import { ImportInputs, ProjectActions } from '../components/import_menu';
import { PremisesDrawer } from '../components/premises_drawer';
import { TravelersPanel } from '../components/travelers_panel';
import { useMiceImport, type MiceImport } from '../hooks/use_mice_import';
import { useMiceProject } from '../hooks/use_mice_orders';
import { useRailOpen } from '../hooks/use_rail_open';

function headerLine(project: MiceProjectView): string {
  const period =
    project.eventDate && project.eventEnd
      ? t('mice.project.period', {
          start: formatRawDate(project.eventDate, null),
          end: formatRawDate(project.eventEnd, null),
        })
      : null;
  return [
    period,
    project.client?.name,
    t('mice.project.headerLine', { requester: project.requester.name ?? project.requester.email }),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O organizador encerrou as compras pelo chat: o motivo de as ações de compra estarem
 *  desligadas (o servidor recusa toda compra nova do evento). */
function PurchasesClosedNotice({ closedAt }: Readonly<{ closedAt: string }>): React.JSX.Element {
  const title = t('mice.events.purchasesClosed');
  return (
    <Card>
      <div role="status" aria-label={title} className="flex flex-wrap items-center gap-2 p-3">
        <ToneChip tone="amber">{title}</ToneChip>
        <span className="text-meta text-foreground">
          {t('mice.project.purchasesClosedNote', { date: formatRawDate(closedAt, null) })}
        </span>
      </div>
    </Card>
  );
}

/** Uma linha: nome do evento, período, cliente e solicitante, e as ações à direita. */
function ProjectHeader({
  project,
  canManage,
  importer,
  onSummary,
  leading,
}: Readonly<{
  project: MiceProjectView | undefined;
  canManage: boolean;
  importer: MiceImport;
  onSummary: () => void;
  /** Elemento opcional antes do título (o botão de recolher a coluna de filtros). */
  leading?: React.ReactNode;
}>): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 [&>header]:mb-0">
        {leading}
        <PageSectionHead title={project?.name ?? ''} />
        {project ? (
          <span className="text-meta text-muted-foreground">{headerLine(project)}</span>
        ) : null}
      </div>
      <ProjectActions
        importer={importer}
        project={project}
        canManage={canManage}
        onSummary={onSummary}
      />
    </div>
  );
}

export function MiceProjectPage(): React.JSX.Element {
  const { projectId = '' } = useParams({ strict: false });
  const project = useMiceProject(projectId).data;
  const importer = useMiceImport(projectId);
  const authz = useAuthz();
  const [premisesOpen, setPremisesOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const rail = useRailOpen();
  const canManage = authz.has('mice:manage');
  const closedAt = project?.purchasesClosedAt ?? null;

  return (
    <Scaffold
      body={
        <div className="mx-auto flex w-full flex-col gap-4">
          <ProjectHeader
            project={project}
            canManage={canManage}
            importer={importer}
            onSummary={() => {
              setSummaryOpen(true);
            }}
            leading={
              <Button
                variant="outline"
                size="sm"
                aria-expanded={rail.open}
                aria-controls={FILTERS_RAIL_ID}
                onClick={rail.toggle}
              >
                {t(rail.open ? 'mice.filter.collapse' : 'mice.filter.open')}
              </Button>
            }
          />
          <ImportInputs importer={importer} />
          {closedAt ? <PurchasesClosedNotice closedAt={closedAt} /> : null}
          {project ? (
            <PremisesDrawer
              projectId={projectId}
              project={project}
              canManage={canManage}
              open={premisesOpen}
              onClose={() => {
                setPremisesOpen(false);
              }}
            />
          ) : null}
          {project ? (
            <EventSetupChecklist
              project={project}
              travelers={project.phaseCounts.all}
              canManage={canManage}
              onPremises={() => {
                setPremisesOpen(true);
              }}
              onImport={importer.pick}
            />
          ) : null}
          <TravelersPanel
            projectId={projectId}
            canPurchase={authz.has('mice:purchase')}
            purchasesOpen={closedAt === null}
            canManage={canManage}
            premisesReady={project?.premisesCheckedAt != null}
            priceTolerance={project?.priceTolerance ?? 0}
            project={project}
            railOpen={rail.open}
            summaryOpen={summaryOpen}
            onSummaryClose={() => {
              setSummaryOpen(false);
            }}
            onPremises={() => {
              setPremisesOpen(true);
            }}
          />
        </div>
      }
    />
  );
}
